import {
  createAsyncThunk,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";
import { selectUser } from "../user/userSlice";
import { api } from "../../common/setupApi";

export const getStliteApps = createAsyncThunk(
  "stlite/getApps",
  async (_, { getState, rejectWithValue }) => {
    const userPk = selectUser(getState()).data.pk;
    return api
      .get("ai/apps/")
      .then((res) => {
        return { userPk, data: res.data };
      })
      .catch((err) => {
        return rejectWithValue(err.response?.data);
      });
  },
  {
    condition: (params, { getState }) => {
      if (params?.ignoreCondition) {
        return true;
      }
      // https://redux-toolkit.js.org/api/createAsyncThunk#canceling-before-execution
      const { status } = selectStliteApps(getState());
      if (status === "fulfilled" || status === "pending") {
        // Already fetched or in progress, don't need to re-fetch
        return false;
      }
    },
  }
);

export const getStliteAppDetails = createAsyncThunk(
  "stlite/getAppDetails",
  async ({ appId }, { dispatch, rejectWithValue }) => {
    dispatch(stliteActions.startGetDetails(appId));
    return api
      .get(`ai/apps/${appId}/`)
      .then((res) => {
        return { id: appId, data: res.data };
      })
      .catch((err) => {
        return rejectWithValue({ data: err.response?.data, id: appId });
      });
  },
  {
    condition: (params, { getState }) => {
      if (params?.ignoreCondition) {
        return true;
      }
      // https://redux-toolkit.js.org/api/createAsyncThunk#canceling-before-execution
      const selectedApp = selectSelectedStliteApp(getState());
      const status = selectedApp.details?.status;

      if (status === "fulfilled" || status === "pending") {
        // Already fetched or in progress, don't need to re-fetch
        return false;
      }
    },
  }
);

export const getStliteAppChat = createAsyncThunk(
  "stlite/getAppChat",
  async ({ appId }, { dispatch, rejectWithValue }) => {
    dispatch(stliteActions.startGetChat(appId));
    return api
      .get(`ai/chat/${appId}/`)
      .then((res) => {
        return { id: appId, data: res.data };
      })
      .catch((err) => {
        return rejectWithValue({ data: err.response?.data, id: appId });
      });
  },
  {
    condition: (params, { getState }) => {
      if (params?.ignoreCondition) {
        return true;
      }
      // https://redux-toolkit.js.org/api/createAsyncThunk#canceling-before-execution
      const selectedApp = selectSelectedStliteApp(getState());
      const status = selectedApp.chat?.status;

      if (status === "fulfilled" || status === "pending") {
        // Already fetched or in progress, don't need to re-fetch
        return false;
      }
    },
  }
);

export const toggleStliteApp = (id) => async (dispatch, getState) => {
  const state = getState();
  const userPk = selectUser(state).data.pk;
  dispatch(stliteActions.toggleApp({ userPk, id }));
};

const initialState = {
  selectedId: null,
  lastEvent: null,
  apps: {
    status: "idle",
    error: null,
    data: [],
  },
};

const slice = createSlice({
  name: "stlite",
  initialState,
  reducers: {
    setLastEvent: (state, action) => {
      state.lastEvent = action.payload;
    },
    resetLastEvent: (state) => {
      state.lastEvent = state.selectedId !== null ? "load" : null;
    },
    toggleApp: (state, action) => {
      const { userPk, id } = action.payload;
      localStorage.setItem(`user${userPk}_stlite_app_displayed`, id);
      state.selectedId = id;
      state.lastEvent = id === null ? null : "load";
    },
    addApp: (state, action) => {
      state.apps.data.push(action.payload);
      state.lastEvent = "create";
    },
    deleteApp: (state, action) => {
      state.lastEvent = null;
      state.apps.data = state.apps.data.filter((d) => d.id !== action.payload);
    },
    startGetDetails: (state, action) => {
      state.apps.data = state.apps.data.map((d) =>
        d.id === action.payload ? { ...d, details: { status: "pending" } } : d
      );
    },
    startGetChat: (state, action) => {
      state.apps.data = state.apps.data.map((d) =>
        d.id === action.payload ? { ...d, chat: { status: "pending" } } : d
      );
    },
    addChat: (state, action) => {
      const { id, newChat } = action.payload;
      state.apps.data = state.apps.data.map((d) =>
        d.id === id
          ? {
              ...d,
              chat: {
                ...d.chat,
                data: [...d.chat.data, newChat],
              },
            }
          : d
      );
    },
    setAppName: (state, action) => {
      const { id, name } = action.payload;
      state.apps.data = state.apps.data.map((d) =>
        d.id === id ? { ...d, name } : d
      );
    },
    setAppDetails: (state, action) => {
      state.apps.data = state.apps.data.map((d) =>
        d.id === action.payload.id
          ? { ...d, details: { ...d.details, data: action.payload } }
          : d
      );
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getStliteApps.pending, (state) => {
        state.apps.status = "pending";
        state.apps.error = null;
      })
      .addCase(getStliteApps.fulfilled, (state, action) => {
        state.apps.status = "fulfilled";
        state.apps.data = action.payload.data;

        const userPk = action.payload.userPk;

        const localStorageValue = localStorage.getItem(
          `user${userPk}_stlite_app_displayed`
        );

        if (localStorageValue) {
          // utile si un autre coach supprime une app
          const ids = action.payload.data.map((d) => d.id);
          const hasBeenDeleted = !ids.includes(Number(localStorageValue));

          if (hasBeenDeleted) {
            localStorage.setItem(`user${userPk}_stlite_app_displayed`, "");
            state.selectedId = null;
            state.lastEvent = null;
          } else {
            state.selectedId = Number(localStorageValue);
            state.lastEvent = "load";
          }
        }
      })
      .addCase(getStliteApps.rejected, (state, action) => {
        state.apps.status = "rejected";
        state.apps.error = action.payload;
      })
      // ---
      // get details
      // ---
      .addCase(getStliteAppDetails.pending, () => {})
      .addCase(getStliteAppDetails.fulfilled, (state, action) => {
        state.apps.data = state.apps.data.map((d) =>
          d.id === action.payload.id
            ? {
                ...d,
                details: { data: action.payload.data, status: "fulfilled" },
              }
            : d
        );
      })
      .addCase(getStliteAppDetails.rejected, (state, action) => {
        state.apps.data = state.apps.data.map((d) =>
          d.id === action.payload.id
            ? { ...d, details: { status: "rejected" } }
            : d
        );
      })
      // ---
      // get chats
      // ---
      .addCase(getStliteAppChat.pending, () => {})
      .addCase(getStliteAppChat.fulfilled, (state, action) => {
        state.apps.data = state.apps.data.map((d) =>
          d.id === action.payload.id
            ? {
                ...d,
                chat: { data: action.payload.data, status: "fulfilled" },
              }
            : d
        );
      })
      .addCase(getStliteAppChat.rejected, (state, action) => {
        state.apps.data = state.apps.data.map((d) =>
          d.id === action.payload.id
            ? { ...d, chat: { status: "rejected" } }
            : d
        );
      })
      .addCase("auth/logout", () => initialState);
  },
});

export const stliteActions = slice.actions;

const _selectStlite = (state) => state.stlite;

export const selectStliteApps = createSelector(
  [(state) => _selectStlite(state)],
  (stlite) => {
    const data = stlite.apps.data || [];
    const status = stlite.apps.status;
    const error = stlite.apps.error;
    const isLoading = status === "pending";
    return {
      data,
      status,
      error,
      isLoading,
    };
  }
);

export const selectSelectedStliteId = (state) =>
  _selectStlite(state).selectedId;

export const selectIsAppSelected = createSelector(
  [(state) => selectSelectedStliteId(state)],
  (selectedId) =>
    selectedId !== null && selectedId !== undefined && selectedId !== ""
);

export const selectSelectedStliteApp = createSelector(
  [
    (state) => _selectStlite(state).selectedId,
    (state) => selectStliteApps(state).data,
  ],
  (selectedId, data) => data.find((d) => d.id === selectedId) || {}
);

export const selectStliteTemplates = createSelector(
  [(state) => _selectStlite(state)],
  (stlite) => {
    const data = stlite.templates.data || [];
    const status = stlite.templates.status;
    const error = stlite.templates.error;
    const isLoading = status === "pending";
    return { data, status, error, isLoading };
  }
);

export const selectStliteLastEvent = (state) => _selectStlite(state).lastEvent;

export default slice.reducer;
